/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService, IAuthServiceStatusHistorySearchFilters } from 'app/um/services/auth-service';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class AuthServicesStatusHistorySequential extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.string({ changeHandler: 'reload' })
    protected authId: string | null = null;

    protected authServiceStatusHistory: UM.AuthServiceStatusHistory[] = [];

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 10;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadAuthServiceStatusHistory();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadAuthServiceStatusHistory(): Promise<void> {
        if(!this.authId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            includeInactive: true
        };

        const includes = [
            'audit.start',
            'audit.start.user',
            'service',
            'service.definition',
            'status',
            'status.reason'
        ];

        const fields = [
            'audit.start.timestamp',
            'audit.start.user.description',
            'sequence',
            'service.definition.code',
            'service.definition.description',
            'service.sequence',
            'status.description',
            'status.reason.description'
        ];

        const filters: IAuthServiceStatusHistorySearchFilters = {
            onlyServices: true
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadServiceStatusHistoryByAuth(this.authId, options, includes, fields, filters);
            this.authServiceStatusHistory = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
